export interface InvoiceRenewalSubscriptionLike {
  payment_provider?: string | null;
  payment_recurrency?: string | null;
  /** Backend: an early PIX renewal keeps the current limits until this instant. */
  pending_cycle_reset_at?: string | null;
  plan?: {
    value?: number | string | null;
    value_yearly?: number | string | null;
  } | null;
}

export interface PixOrderLike {
  status?: string | null;
  expires_at?: string | null;
}

const LIVE_STATUSES = new Set(['waiting_payment', 'pending']);

/**
 * Whether `/invoice` may offer "Gerar código PIX" (createPixRenewalOrder):
 * a PIX subscriber outside the mobile app whose renewal is not paid yet.
 *
 * - A set `pending_cycle_reset_at` means the renewal is already paid, also in
 *   the up-to-an-hour gap after its instant before the hourly job clears it.
 * - The plan must have a PIX renewal price, as the backend's
 *   `validatePixRenewalEligibility` requires (`invalid_plan_value` otherwise,
 *   e.g. after a downgrade to FREE).
 */
export function canGenerateRenewalCode({ subscription, embedded, isPaid }: {
  subscription: InvoiceRenewalSubscriptionLike | null | undefined;
  embedded: boolean;
  isPaid: boolean;
}): boolean {
  if (embedded || isPaid || !subscription) return false;
  if (subscription.payment_provider !== 'pagarme') return false;
  if (subscription.pending_cycle_reset_at) return false;
  const plan = subscription.plan;
  if (!plan) return false;
  const price = subscription.payment_recurrency === 'yearly' ? plan.value_yearly : plan.value;
  return Number(price ?? 0) > 0;
}

/**
 * Whether a PIX order can still be paid: waiting for payment and not past its
 * `expires_at` (Pagar.me may keep reporting a waiting or failed status after
 * the PIX expired). An order without a readable expiry counts as live, so no
 * second code is offered next to it.
 */
export function isLivePixOrder(order: PixOrderLike | null | undefined, now: Date): boolean {
  if (!order || !LIVE_STATUSES.has(order.status ?? '')) return false;
  if (!order.expires_at) return true;
  const expiresAt = new Date(order.expires_at).getTime();
  return Number.isNaN(expiresAt) || expiresAt > now.getTime();
}
